import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    Button,
    InputAdornment
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { api, endpoints } from '../config/api';

interface TourStat {
    tourId: string;
    tourName: string;
    totalViews: number;
    dailyStats: { date: string; views: number }[];
}

interface Tour {
    id: string; // or _id
    name: string;
}

const TourStats: React.FC = () => {
    const [stats, setStats] = useState<TourStat[]>([]);
    const [summary, setSummary] = useState({ totalViews: 0, tourCount: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tours, setTours] = useState<Tour[]>([]);

    // Filters
    const [selectedTourId, setSelectedTourId] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    useEffect(() => {
        fetchTours();
        fetchStats();
    }, []);

    // Re-fetch stats when filters change? Or trigger manually?
    // Let's trigger manually or on effect with debounce, but button is safer.
    // Actually, common pattern is auto-fetch on change.
    useEffect(() => {
        fetchStats();
    }, [selectedTourId, startDate, endDate]);

    const fetchTours = async () => {
        try {
            const response = await api.get(endpoints.tours);
            if (response.data && response.data.success) {
                // Ensure we handle both id and _id if necessary, but typically response data has id mapped
                const tourList = response.data.data.map((t: any) => ({
                    id: t.id || t._id,
                    name: t.name
                }));
                setTours(tourList);
            }
        } catch (err) {
            console.error('Error fetching tours list:', err);
        }
    };

    const fetchStats = async () => {
        try {
            setLoading(true);
            const params: any = {};
            if (selectedTourId) params.tourId = selectedTourId;
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const response = await api.get(endpoints.tourStatsAggregate, { params });
            if (response.data && response.data.success) {
                setStats(response.data.data.tours);
                setSummary(response.data.data.summary);
                setError(null);
            } else {
                setError('Failed to load statistics');
            }
        } catch (err) {
            console.error('Error fetching tour stats:', err);
            setError('Error loading statistics. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setSelectedTourId('');
        setStartDate('');
        setEndDate('');
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 4, color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <BarChartIcon sx={{ mr: 2, fontSize: 40 }} />
                Statistiche Tour
            </Typography>

            {/* Filters Section */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="h6" color="text.secondary">Filtri</Typography>
                </Box>
                <Grid container spacing={3} alignItems="center">
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            select
                            fullWidth
                            label="Filtra per Tour"
                            value={selectedTourId}
                            onChange={(e) => setSelectedTourId(e.target.value)}
                            variant="outlined"
                        >
                            <MenuItem value="">
                                <em>Tutti i Tour</em>
                            </MenuItem>
                            {tours.map((tour) => (
                                <MenuItem key={tour.id} value={tour.id}>
                                    {tour.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            label="Data Inizio"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            label="Data Fine"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            onClick={clearFilters}
                            disabled={!selectedTourId && !startDate && !endDate}
                        >
                            Resetta Filtri
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card sx={{ height: '100%', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                                <CardContent>
                                    <Typography gutterBottom sx={{ opacity: 0.8 }}>
                                        Visualizzazioni Totali
                                    </Typography>
                                    <Typography variant="h3" component="div" fontWeight="bold">
                                        {summary.totalViews}
                                    </Typography>
                                    <Box display="flex" alignItems="center" mt={1} sx={{ opacity: 0.8 }}>
                                        <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                                        <Typography variant="body2">
                                            nel periodo selezionato
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card sx={{ height: '100%', bgcolor: 'secondary.main', color: 'secondary.contrastText' }}>
                                <CardContent>
                                    <Typography gutterBottom sx={{ opacity: 0.8 }}>
                                        Tour Coinvolti
                                    </Typography>
                                    <Typography variant="h3" component="div" fontWeight="bold">
                                        {summary.tourCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Stats Table */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell><Typography fontWeight="bold">Tour</Typography></TableCell>
                                    <TableCell align="right"><Typography fontWeight="bold">Visualizzazioni Totali</Typography></TableCell>
                                    <TableCell align="right"><Typography fontWeight="bold">Media Giornaliera</Typography></TableCell>
                                    <TableCell align="right"><Typography fontWeight="bold">Data Recente</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.length > 0 ? (
                                    stats.map((row) => {
                                        const daysWithViews = row.dailyStats?.length || 1;
                                        const lastDate = row.dailyStats?.length > 0
                                            ? new Date(row.dailyStats[row.dailyStats.length - 1].date).toLocaleDateString()
                                            : '-';

                                        return (
                                            <TableRow key={row.tourId} hover>
                                                <TableCell component="th" scope="row">
                                                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                                        {row.tourName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="h6" color="text.primary">
                                                        {row.totalViews}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {(row.totalViews / daysWithViews).toFixed(1)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {lastDate}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                            <Typography color="text.secondary">Nessun dato disponibile con i filtri correnti</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default TourStats;
